// Shared amount parsing + formatting for editable numeric inputs and
// read-only money displays. Conventions (HMS team, July 2026):
//
//   - Thousands separator "," and decimal point "." everywhere, regardless
//     of browser locale. This matters because <input type="number"> lets the
//     BROWSER render the value per its locale — on German-locale machines a
//     backend decimal string like "300.000" displays as "300,000", which a
//     trader reads as three hundred thousand. Amount inputs must therefore
//     be text inputs whose display we own (see components/ui/AmountInput.js).
//
//   - Editable amounts never show phantom decimals: backend DecimalFields
//     serialize to fixed-scale strings ("150.00" for 2dp tonnage,
//     "801.0000" for 4dp prices). Trailing zeros are stripped so decimals
//     appear only when the value genuinely carries them or the user types
//     them. Genuine cents are PRESERVED in editable fields — rounding an
//     editable value would silently corrupt it on the next save.
//
//   - Read-only money displays show no cents: rounded to nearest, grouped.
//     (Same convention as the enquiry form's credit-balance strip.)

// Canonicalize any input (user text, API decimal string, number) to a plain
// dot-decimal string: "1,250.50" -> "1250.50", ".5" -> "0.5", "300." ->
// "300", ""/null/undefined/non-numeric -> "".
export const canonicalizeAmount = (raw) => {
  if (raw === null || raw === undefined) return '';
  let s = String(raw).trim().replace(/,/g, '');
  if (s === '') return '';
  if (s.startsWith('.')) s = `0${s}`;
  if (s.endsWith('.')) s = s.slice(0, -1);
  return /^-?\d+(\.\d+)?$/.test(s) ? s : '';
};

// "801.0000" -> "801"; "300.50" -> "300.5"; "300" -> "300".
export const stripTrailingZeros = (value) => {
  const canon = canonicalizeAmount(value);
  if (canon === '' || !canon.includes('.')) return canon;
  return canon.replace(/0+$/, '').replace(/\.$/, '');
};

// True when `raw` carries more decimal places than the backend column can
// store -- e.g. a bunker price typed as 512.345 against
// InvoiceLineItem.unit_price, which is DecimalField(14, 2).
//
// Why the inputs check this instead of letting the save find out: DRF refuses
// the whole payload with a per-row error, and a nested error is the one shape
// the screens historically rendered as "[object Object]". Refusing the extra
// digit at the keystroke keeps the operator on a value that can actually be
// saved. Refuse, never silently truncate -- a quietly shortened price is a
// wrong price nobody sees (working agreement s2).
export const exceedsDecimals = (raw, maxDecimals) => {
  if (maxDecimals === null || maxDecimals === undefined) return false;
  const canon = canonicalizeAmount(raw);
  if (canon === '' || !canon.includes('.')) return false;
  return canon.split('.')[1].length > maxDecimals;
};

const groupThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// Display form for an editable amount: trailing zeros stripped, integer part
// grouped, decimals kept only when genuine. "1234567.50" -> "1,234,567.5".
export const formatAmount = (value) => {
  const canon = stripTrailingZeros(value);
  if (canon === '') return '';
  const negative = canon.startsWith('-');
  const [intPart, fracPart] = (negative ? canon.slice(1) : canon).split('.');
  const grouped = `${negative ? '-' : ''}${groupThousands(intPart)}`;
  return fracPart ? `${grouped}.${fracPart}` : grouped;
};

// Read-only money display: rounded to nearest whole unit, grouped, no cents.
// formatMoneyDisplay("4500000") -> "4,500,000";
// formatMoneyDisplay(120150.75, { symbol: '$' }) -> "$120,151".
export const formatMoneyDisplay = (value, { symbol = '' } = {}) => {
  const canon = canonicalizeAmount(value);
  if (canon === '') return '';
  const rounded = Math.round(Number(canon));
  const negative = rounded < 0;
  const grouped = groupThousands(String(Math.abs(rounded)));
  return `${negative ? '-' : ''}${symbol}${grouped}`;
};
